import tzlookup from 'tz-lookup';
import { geocodeLocation } from '@/lib/api/client';

export interface LocationPreset {
  name: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

export const POPULAR_LOCATIONS: LocationPreset[] = [
  { name: 'New Delhi, Delhi, India', latitude: 28.6139, longitude: 77.2090, timezone: 'Asia/Kolkata' },
  { name: 'Mumbai, Maharashtra, India', latitude: 19.0760, longitude: 72.8777, timezone: 'Asia/Kolkata' },
  { name: 'Bengaluru, Karnataka, India', latitude: 12.9716, longitude: 77.5946, timezone: 'Asia/Kolkata' },
  { name: 'Kolkata, West Bengal, India', latitude: 22.5726, longitude: 88.3639, timezone: 'Asia/Kolkata' },
  { name: 'Chennai, Tamil Nadu, India', latitude: 13.0827, longitude: 80.2707, timezone: 'Asia/Kolkata' },
  { name: 'New York, New York, USA', latitude: 40.7128, longitude: -74.0060, timezone: 'America/New_York' },
  { name: 'London, Greater London, United Kingdom', latitude: 51.5074, longitude: -0.1278, timezone: 'Europe/London' },
  { name: 'Sydney, New South Wales, Australia', latitude: -33.8688, longitude: 151.2093, timezone: 'Australia/Sydney' }
];

export class LocationResolver {
  /**
   * Search location suggestions via our backend geocode endpoint,
   * removing duplicates and maintaining fallback to presets.
   */
  static async searchSuggestions(query: string): Promise<LocationPreset[]> {
    const trimmed = query.trim();
    if (!trimmed || trimmed.length < 2) return [];

    const lowerQuery = trimmed.toLowerCase();
    
    // Local popular presets match (used as fallback when API fails or returns nothing)
    const localMatches = POPULAR_LOCATIONS.filter((loc) =>
      loc.name.toLowerCase().includes(lowerQuery)
    );

    try {
      const apiResults = await geocodeLocation(trimmed);

      if (!apiResults || apiResults.length === 0) {
        return localMatches;
      }

      const deduplicated: LocationPreset[] = [];
      const seenNames = new Set<string>();

      for (const item of apiResults) {
        const rawName = (item.name || '').trim();
        const normKey = rawName.toLowerCase();
        if (!normKey || seenNames.has(normKey)) {
          continue;
        }
        seenNames.add(normKey);

        const lat = typeof item.latitude === 'number' ? Number(item.latitude.toFixed(4)) : 0;
        const lon = typeof item.longitude === 'number' ? Number(item.longitude.toFixed(4)) : 0;
        const timezone = item.timezone || LocationResolver.estimateIanaTimezone(lat, lon);

        deduplicated.push({
          name: rawName,
          latitude: lat,
          longitude: lon,
          timezone
        });
      }

      if (deduplicated.length === 0) {
        return localMatches;
      }

      return deduplicated.slice(0, 8);
    } catch (err) {
      return localMatches;
    }
  }

  /**
   * Exact polygon boundary IANA timezone lookup via tz-lookup library.
   */
  static estimateIanaTimezone(lat: number, lon: number, country?: string): string {
    try {
      return tzlookup(lat, lon);
    } catch (err) {
      return 'UTC';
    }
  }

  /**
   * Resolve location against popular presets. Returns null if unrecognised.
   */
  static resolve(locationInput: string): LocationPreset | null {
    const trimmed = locationInput.trim().toLowerCase();
    const match = POPULAR_LOCATIONS.find((loc) => loc.name.toLowerCase().includes(trimmed));
    return match || null;
  }
}
